import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../context/LanguageContext";
import { Crown, Trophy, Medal, Award } from "lucide-react";

const RANK_BADGE = {
  1: { bg: "bg-gradient-to-br from-[#F59E0B] to-[#EA580C]", text: "text-white", glow: "shadow-[0_0_20px_rgba(245,158,11,0.4)]" },
  2: { bg: "bg-gradient-to-br from-[#94A3B8] to-[#64748B]", text: "text-white", glow: "" },
  3: { bg: "bg-gradient-to-br from-[#B45309] to-[#78350F]", text: "text-white", glow: "" },
};

function initialsOf(name = "") {
  const parts = String(name).trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function Avatar({ name, rank }) {
  const ring = rank === 1
    ? "ring-[#FFD700]"
    : rank === 2
      ? "ring-[#94A3B8]"
      : rank === 3
        ? "ring-[#B45309]"
        : "ring-[#7C3AED]/40";
  return (
    <div
      className={`w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-[#1A1A1E] via-[#2A2A30] to-[#0A0A0A] text-white font-heading text-sm tracking-wide ring-2 ${ring} flex-shrink-0`}
      aria-hidden
      data-testid={`hof-avatar-${rank}`}
    >
      {initialsOf(name)}
    </div>
  );
}

function RankTrophy({ rank }) {
  if (rank === 1) return <Crown className="w-4 h-4 text-[#FFD700] drop-shadow-[0_0_8px_rgba(255,215,0,0.5)] flex-shrink-0" aria-hidden />;
  if (rank === 2) return <Trophy className="w-4 h-4 text-[#94A3B8] flex-shrink-0" aria-hidden />;
  if (rank === 3) return <Medal className="w-4 h-4 text-[#B45309] flex-shrink-0" aria-hidden />;
  return <Award className="w-4 h-4 text-[#7C3AED] flex-shrink-0" aria-hidden />;
}

function RankBadge({ rank }) {
  const cfg = RANK_BADGE[rank] || { bg: "bg-[#E4E4E7] dark:bg-[#2A2A30]", text: "text-[#71717A]", glow: "" };
  return (
    <div
      className={`w-10 h-10 flex items-center justify-center font-heading text-lg ${cfg.bg} ${cfg.text} ${cfg.glow} flex-shrink-0`}
      aria-label={`rank ${rank}`}
    >
      {rank}
    </div>
  );
}

export default function HallOfFame({ limit = 5 }) {
  const { t } = useT();
  const [players, setPlayers] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/hall-of-fame?limit=${limit}`);
        if (alive) setPlayers(data || []);
      } catch (e) {
        if (alive) setPlayers([]);
      }
    })();
    return () => { alive = false; };
  }, [limit]);

  if (players === null) return null; // silent while loading, no layout shift

  return (
    <section data-testid="hall-of-fame" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#F59E0B] to-[#EA580C] flex items-center justify-center shadow-[0_0_24px_rgba(245,158,11,0.35)]">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#EA580C]">{t("hof.kicker")}</div>
            <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wider">{t("hof.title")}</h2>
          </div>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">
          {t("hof.subtitle")}
        </div>
      </div>

      {players.length === 0 ? (
        <div
          data-testid="hof-empty"
          className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#EA580C] p-8 text-center"
        >
          <Trophy className="w-8 h-8 text-[#EA580C] mx-auto mb-3 opacity-60" />
          <div className="font-heading text-xl uppercase tracking-wider text-[#09090B] dark:text-[#F4F4F5]">
            {t("hof.empty")}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mt-2">
            {t("hof.emptyBody")}
          </div>
        </div>
      ) : (
        <ol className="space-y-2" data-testid="hof-list">
          {players.map((p) => {
            const isFirst = p.rank === 1;
            return (
              <li
                key={p.user_id}
                data-testid={`hof-row-${p.rank}`}
                className={`bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 px-4 py-3 flex items-center gap-4 transition-colors hover:border-[#7C3AED] ${
                  isFirst ? "border-l-[#F59E0B]" : p.rank === 2 ? "border-l-[#94A3B8]" : p.rank === 3 ? "border-l-[#B45309]" : "border-l-[#E4E4E7] dark:border-l-[#2A2A30]"
                }`}
              >
                <RankBadge rank={p.rank} />
                <Avatar name={p.username} rank={p.rank} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <RankTrophy rank={p.rank} />
                    {isFirst && (
                      <Crown
                        data-testid="hof-crown"
                        className="w-4 h-4 text-[#F59E0B] drop-shadow-[0_0_8px_rgba(245,158,11,0.5)] flex-shrink-0"
                        aria-label="champion"
                      />
                    )}
                    <span className="font-heading text-lg uppercase tracking-wide truncate text-[#09090B] dark:text-[#F4F4F5]">
                      <Link to={`/players/${encodeURIComponent(p.username)}`} className="hover:text-[#7C3AED] transition-colors" data-testid={`hof-profile-link-${p.rank}`}>
                        {p.username}
                      </Link>
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                      · {p.league_count} {t("pp.seasons")}
                    </span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mt-0.5">
                    {p.played} {t("st.played")} · {p.won}{t("st.wins")} {p.drawn}{t("st.draws")} {p.lost}{t("st.losses")} · {t("st.gd")} {p.gd >= 0 ? "+" : ""}{p.gd}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className={`font-heading text-2xl leading-none ${isFirst ? "text-[#EA580C]" : "text-[#09090B] dark:text-[#F4F4F5]"}`}>
                    {p.points}
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#71717A]">{t("hof.pts")}</div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
